import { lazy } from 'react'
import { redirect } from 'react-router-dom'

import { PurchaseRequestDetailDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { purchaseRequestDisplayTitle } from 'modules/procurement/lib/purchaseRequestTitle'

const PurchaseRequestDetailHeader = lazy(
  () => import('modules/procurement/components/purchaseRequests/PurchaseRequestDetailHeader')
)

const MyPurchaseRequestDetailSummaryPage = lazy(
  () =>
    import('modules/procurement/pages/my_purchases/MyPurchaseRequestDetailPage/MyPurchaseRequestDetailSummaryPage')
)

const MyPurchaseRequestDetailAttachmentsPage = lazy(
  () =>
    import('modules/procurement/pages/my_purchases/MyPurchaseRequestDetailPage/MyPurchaseRequestDetailAttachmentsPage')
)

const MyPurchaseRequestDetailCommentsPage = lazy(
  () =>
    import('modules/procurement/pages/my_purchases/MyPurchaseRequestDetailPage/MyPurchaseRequestDetailCommentsPage')
)

const PurchaseRequestDetailFormsPage = lazy(
  () => import('modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestDetailFormsPage')
)

const NewRequestPage = lazy(() => import('modules/procurement/pages/my_purchases/NewRequestPage'))
const RootPage = lazy(() => import('modules/procurement/pages/my_purchases/RootPage'))

export const myPurchasesRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.myPurchases.path,
      element: <RootPage />,
      handle: {
        module: 'my_spending',
        meta: () => ({
          title: i18n.t('procurement.purchase_requests.my_purchase_requests.title'),
          illustration: 'money-spreadsheet',
        }),
      },
      children: [
        {
          path: navigation.myPurchases.newRequest.path,

          handle: {
            meta: () => ({
              title: i18n.t('procurement.new_request.breadcrumb'),
              illustration: 'money-spreadsheet',
              description: i18n.t('procurement.new_request.description'),
            }),
          },
          element: <NewRequestPage />,
        },
        {
          path: navigation.myPurchases.detail.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params, session }) => {
              return watchQuery({
                query: PurchaseRequestDetailDocument,
                variables: {
                  id: params.id ?? '',
                  companyId: session.company.id,
                },
                fetchPolicy: 'cache-and-network',
              }).map(({ data }) => {
                const purchaseRequest = data?.procurement?.purchaseRequest
                const isProcurementV2FeatureEnabled =
                  !!data?.features?.featuresConnection?.edges?.some(
                    (e) => e.node.name === 'procurement_v2' && e.node.enabled
                  )

                return {
                  title: purchaseRequest ? purchaseRequestDisplayTitle(purchaseRequest) : undefined,
                  headerComponent: purchaseRequest ? (
                    <PurchaseRequestDetailHeader
                      purchaseRequest={purchaseRequest}
                      onlyOwnRequests
                      isWorkflowsEnabled={isProcurementV2FeatureEnabled}
                    />
                  ) : null,
                  subNavigation: purchaseRequest && [
                    {
                      id: 'summary',
                      title: i18n.t('procurement.purchase_requests.detail.tabs.summary'),
                      href: navigation.myPurchases.detail.summary.fullPath({
                        id: purchaseRequest.id,
                      }),
                      isVisible: true,
                    },
                    {
                      id: 'attachments',
                      title: i18n.t('procurement.purchase_requests.detail.tabs.attachments'),
                      href: navigation.myPurchases.detail.attachments.fullPath({
                        id: purchaseRequest.id,
                      }),
                      isVisible: true,
                    },
                    {
                      id: 'comments',
                      title: i18n.t('procurement.purchase_requests.detail.tabs.comments'),
                      href: navigation.myPurchases.detail.comments.fullPath({
                        id: purchaseRequest.id,
                      }),
                      isVisible: true,
                    },
                    {
                      id: 'forms',
                      title: i18n.t('procurement.purchase_requests.detail.tabs.forms'),
                      href: navigation.myPurchases.detail.forms.fullPath({
                        id: purchaseRequest.id,
                      }),
                      isVisible: true,
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              index: true,
              loader: ({ params }) => {
                return redirect(
                  navigation.myPurchases.detail.summary.fullPath({
                    id: params.id ?? '',
                  })
                )
              },
            },
            {
              path: navigation.myPurchases.detail.summary.path,
              element: <MyPurchaseRequestDetailSummaryPage />,
            },
            {
              path: navigation.myPurchases.detail.attachments.path,
              element: <MyPurchaseRequestDetailAttachmentsPage />,
            },
            {
              path: navigation.myPurchases.detail.comments.path,
              element: <MyPurchaseRequestDetailCommentsPage />,
            },
            {
              path: navigation.myPurchases.detail.forms.path,
              element: <PurchaseRequestDetailFormsPage />,
            },
          ],
        },
      ],
    },
  ]
}
